import WheatSvg from "@icons/wheat.svg?react";
import { Trans } from "@lingui/react/macro";
import React from "react";

import type { ChatMessage } from "../market-ai.api";
import { MarketChart } from "./market-chart";

type Props = {
  message: ChatMessage;
};

/** Minimal markdown: bold (**text**), line breaks */
const renderText = (text: string): React.ReactNode => {
  const lines = text.split("\n");
  return lines.map((line, i) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return (
      <React.Fragment key={i}>
        {parts.map((part, j) =>
          part.startsWith("**") && part.endsWith("**") ? (
            <strong key={j} style={{ color: "#4d5570", fontWeight: 600 }}>
              {part.slice(2, -2)}
            </strong>
          ) : (
            part
          ),
        )}
        {i < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
};

const MarketMessage = ({ message }: Props) => {
  const isUser = message.role === "user";

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div
          className="max-w-[78%] rounded-2xl rounded-tr-sm px-4 py-3 text-base leading-relaxed text-white"
          style={{
            background: "linear-gradient(135deg, #168b6b 0%, #0d6b53 100%)",
          }}
        >
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-y-3">
      {/* Avatar + bubble row */}
      <div className="flex items-start gap-x-2.5">
        <div
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
          style={{
            background: "linear-gradient(135deg, #168b6b 0%, #0d6b53 100%)",
          }}
        >
          <WheatSvg className="h-3.5 w-3.5 text-white" />
        </div>

        <div
          className="max-w-[90%] rounded-2xl rounded-tl-sm px-4 py-3"
          style={{
            background: "#fff",
            boxShadow: "2px 4px 12px 0px rgba(122,128,147,0.18)",
          }}
        >
          <p className="text-base leading-relaxed" style={{ color: "#4d5570" }}>
            {renderText(message.content)}
          </p>
        </div>
      </div>

      {/* Chart */}
      {message.visualization ? (
        <div className="ml-9">
          <MarketChart visualization={message.visualization} />
        </div>
      ) : null}

      {/* Sources */}
      {message.sources && message.sources.length > 0 ? (
        <div className="ml-9 flex flex-wrap gap-1.5">
          {message.sources.map((src, i) => (
            <span
              className="rounded-full px-2.5 py-0.5 text-xs font-medium"
              key={i}
              style={{
                background: "rgba(22,139,107,0.08)",
                border: "1px solid rgba(22,139,107,0.15)",
                color: "#168b6b",
              }}
            >
              <Trans>Zdroj</Trans> {i + 1}: {src}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export { MarketMessage };
